<template>
  <BaseDialog
    type="basic"
    :is-open="isOpen"
    @click="emits('close')"
    :title="$t('organization.inviteMember.title')"
    @close="cancel"
    class="md:w-full"
  >
    <div class="flex flex-col pb-2">
      <BaseEmailChipField
        ref="emailField"
        v-model="emails"
        :label="$t('organization.inviteMember.email')"
        :supporting-text="$t('organization.inviteMember.emailSupportingText')"
        :error="!isEmailsValid ? 'organization.inviteMember.emailError' : ''"
        :excluded-emails="existingMemberEmails"
        required
        class="mb-2"
      />
      <h4 class="text-title-small mt-4 mb-1">{{ $t('organization.roleSection') }}</h4>
      <p class="text-body-medium text-error" v-if="!isRolesValid">
        {{ $t('organization.inviteMember.rolesError') }}
      </p>
      <OrganizationRoleSelect v-model="member.roles" :available-roles="availableRoles" />
      <OrganizationTeamSelect v-if="isOpen" v-model="teamId" class="mt-4" />
      <BaseNativeCheckbox v-model="member.sendEmail" class="mt-4">{{
        $t('member.sendEmail')
      }}</BaseNativeCheckbox>
    </div>
    <template #dismiss>
      <BaseTextButton color="text" @click="cancel" size="M">{{
        $t('common.cancel')
      }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton color="filled" @click="addMember" size="M">{{
        $t('organization.invite')
      }}</BaseTextButton>
    </template>
  </BaseDialog>
</template>
<script setup lang="ts">
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import type { AddMemberBody } from '../composables/useOrganizationSettings'
  import { computed, ref, watch } from 'vue'
  import BaseNativeCheckbox from '../../../core/components/base/checkbox/BaseNativeCheckbox.vue'
  import type { Role } from '../../organization/models/organization-role'
  import OrganizationRoleSelect from './OrganizationRoleSelect.vue'
  import { useMembersStore } from '../../../stores/members.store'
  import { useLocalStorage } from '@vueuse/core'
  import BaseEmailChipField from '../../../core/components/base/form-fields/BaseEmailChipField.vue'
  import OrganizationTeamSelect from './OrganizationTeamSelect.vue'

  interface Props {
    isOpen: boolean
    availableRoles: Role[]
  }
  const props = defineProps<Props>()

  const emits = defineEmits<{
    (e: 'close'): void
    (e: 'memberAdded', member: AddMemberBody): void
  }>()

  const memberStore = useMembersStore()
  const sendEmailLocalStorage = useLocalStorage('organization-member-send-email', true)

  const emails = ref<string[]>([])
  const teamId = ref<string>()
  const isEmailsValid = ref(true)
  const isRolesValid = ref(true)

  const member = ref<Omit<AddMemberBody, 'emails'>>({
    sendEmail: sendEmailLocalStorage.value,
    roles: [],
  })

  const existingMemberEmails = computed(() => {
    return memberStore.members.flatMap((existingMember) =>
      existingMember.email ? [existingMember.email] : [],
    )
  })

  watch(
    () => member.value.sendEmail,
    (newVal) => {
      sendEmailLocalStorage.value = newVal
    },
  )
  watch(
    () => emails.value,
    () => {
      isEmailsValid.value = true
    },
  )

  watch(
    () => props.isOpen,
    (newVal) => {
      if (!newVal) {
        resetForm()
      }
    },
  )

  const resetForm = () => {
    emails.value = []
    teamId.value = undefined
    isEmailsValid.value = true
    member.value.sendEmail = sendEmailLocalStorage.value
    member.value.roles = []
    isRolesValid.value = true
    isEmailsValid.value = true
  }

  const validateRoles = () => {
    isRolesValid.value = member.value.roles.length > 0
  }

  const validateEmails = () => {
    isEmailsValid.value = emails.value.length > 0
  }

  const cancel = () => {
    resetForm()
    emits('close')
  }

  const addMember = () => {
    validateEmails()
    validateRoles()
    if (!isEmailsValid.value || !isRolesValid.value) return
    emits('memberAdded', {
      emails: emails.value,
      sendEmail: member.value.sendEmail,
      roles: member.value.roles,
      ...(teamId.value ? { teamId: teamId.value } : {}),
    })
  }
</script>
